import { useEffect, useMemo, useState } from 'react'
import Select from 'react-select'
import { debounce } from 'lodash'
import { usePaginatedDevices } from '../../../../../../utils/hooks/usePaginatedDevices'
import Label from '../../../../../components/_shared/Label/Label'

// Loads all devices (no user filter) 10 at a time with debounced search.
// Calls onLabelChange(label) when the selection changes so the parent can
// resolve the device name for pill display without a static all-devices query.
const IncidentDeviceAsyncDropdown = ({ value, setFieldValue, onLabelChange }) => {
    const [search, setSearch] = useState('')
    const [selectedValue, setSelectedValue] = useState(null)

    // No userId / isAdmin flags → usePaginatedDevices uses getAllDevices.
    const { data, fetchNextPage, hasNextPage, isFetching } = usePaginatedDevices(search)

    const options = useMemo(() => {
        return data?.pages?.flatMap((page) => {
            const items = Array.isArray(page?.data) ? page.data : []
            return items.map((d) => ({ value: d.value, label: d.label }))
        }) ?? []
    }, [data])

    const handleSearch = useMemo(
        () => debounce((inputValue) => setSearch(inputValue), 300),
        []
    )

    useEffect(() => () => handleSearch.cancel(), [handleSearch])

    // Sync pre-filled value once options load.
    useEffect(() => {
        if (!value) { setSelectedValue(null); return }
        const match = options.find(
            (o) => String(o.value) === String(value)
        )
        if (match) setSelectedValue(match)
    }, [value, options])

    const handleChange = (option) => {
        setSelectedValue(option)
        setFieldValue('device_cd', option?.value ?? '')
        onLabelChange?.(option?.label ?? '')
    }

    return (
        <>
            <Label labelText="Device Name" />
            <Select
                value={selectedValue || null}
                onChange={handleChange}
                onMenuOpen={() => { if (!value) setSearch('') }}
                onMenuClose={() => handleSearch.cancel()}
                options={options}
                onInputChange={(newValue, { action }) => {
                    if (action === 'input-change') handleSearch(newValue)
                    return newValue
                }}
                onMenuScrollToBottom={() => hasNextPage && fetchNextPage()}
                isLoading={isFetching}
                placeholder={isFetching ? 'Loading devices…' : 'Select…'}
                filterOption={null}
                isClearable
                menuPosition="fixed"
                menuPlacement="top"
            />
        </>
    )
}

export default IncidentDeviceAsyncDropdown
